/**
 * 兼容垫片：补齐旧内核浏览器缺失的 Promise.withResolvers。
 *
 * 背景：pdfjs-dist 4.10.38 在主线程 API 与 worker 里**直接**调用
 * `Promise.withResolvers()`（未做特性检测），该 API 只有
 * Chrome/Edge 119+（2023-10）、Firefox 121+、Safari 17.4+（2024-03）、Node 22+
 * 才内置。旧内核（旧版 Safari、旧版 Android WebView、套壳浏览器）一调用就抛
 * `Promise.withResolvers is not a function`，导致「PDF 转图片」「强压缩」直接失败。
 *
 * pdfjs 自己对 Promise.try、Uint8Array.toBase64 都做了特性检测回退，
 * 唯独漏了 withResolvers（上游缺陷），所以只补这一个。
 *
 * 注意：本模块必须**先于** pdfjs 相关代码执行——
 * - 主线程：由 app.ts 首行 import（pdfjs 是按需动态 import 的，顺序有保证）；
 * - Worker：pdfjs 的 worker 跑在独立 JS 全局域，主线程垫片不会传递进去，
 *   由 src/lib/pdfjs-worker.ts 在加载真正的 pdfjs worker 模块前引入。
 */
type WithResolvers<T> = {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

const PromiseCtor = Promise as unknown as {
  withResolvers?: <T>() => WithResolvers<T>;
};

if (typeof PromiseCtor.withResolvers !== 'function') {
  PromiseCtor.withResolvers = <T>() => {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}
